<script setup lang="ts">
import TagComponent from '@/components/contents/interim/components/TagComponent.vue';
import { resolveStoryblokStringOption } from '@/utils/storyblokOption';

interface Feature {
    _uid: string;
    icon: { filename: string; alt: string };
    image?: { filename: string; alt: string };
    title: string;
    description: string;
    mobile_image_focus?: 'left' | 'center' | 'right' | string | { value: string };
}

interface FeatureGridBlok {
    _uid: string;
    headline: string;
    subline: string;
    features: Feature[];
    background_color: string;
}

defineProps<{ blok: FeatureGridBlok }>();

function imageFocusClass(focus: Feature['mobile_image_focus']): string {
    const f = resolveStoryblokStringOption(focus).toLowerCase();
    if (f === 'left') {
        return 'object-left lg:object-center';
    }
    if (f === 'right') {
        return 'object-right lg:object-center';
    }

    return 'object-center';
}
</script>

<template>
    <section
        v-editable="blok"
        class="py-16 xl:py-24"
        :class="blok.background_color === 'warm' ? 'bg-[#FCEEE6]' : blok.background_color === 'light' ? 'bg-[#FCF7F4]' : 'bg-white'"
    >
        <div class="mx-auto flex max-w-[1440px] flex-col items-center px-4">
            <div v-if="blok.headline || blok.subline" class="mb-12 flex max-w-2xl flex-col gap-5 text-center xl:mb-16 xl:gap-6">
                <h2 v-if="blok.headline" class="text-[28px] font-semibold leading-tight text-[#004646] xl:text-4xl">
                    {{ blok.headline }}
                </h2>
                <p v-if="blok.subline" class="text-base font-normal text-[#393734] xl:text-xl">
                    {{ blok.subline }}
                </p>
            </div>

            <!-- Match GiftSection.vue “Services” tiles (Startseite) -->
            <div
                class="mx-auto grid w-full grid-cols-1 gap-20 xl:gap-12"
                :class="
                    blok.features?.length === 2
                        ? 'max-w-3xl md:grid-cols-2 md:gap-12'
                        : 'max-w-7xl xl:grid-cols-3'
                "
            >
                <div
                    v-for="(feature, index) in blok.features"
                    :key="feature._uid"
                    :class="
                        feature.image?.filename
                            ? 'grid grid-cols-2 gap-6 xl:block'
                            : 'flex flex-col items-center gap-5 text-center xl:items-start xl:text-left'
                    "
                >
                    <div
                        v-if="feature.image?.filename"
                        :class="index % 2 !== 0 ? 'order-2 justify-self-end' : 'justify-self-end'"
                    >
                        <img
                            :src="feature.image.filename + '/m/828x0'"
                            :alt="feature.image.alt || feature.title"
                            class="h-[296px] w-full object-cover xl:h-[411px] xl:w-[411px]"
                            :class="imageFocusClass(feature.mobile_image_focus)"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                    <img
                        v-else-if="feature.icon?.filename"
                        :src="feature.icon.filename + '/m/120x0'"
                        :alt="feature.icon.alt || feature.title"
                        class="h-12 w-12 xl:h-[60px] xl:w-[60px]"
                        loading="lazy"
                    />

                    <div
                        class="xl:mt-6"
                        :class="index % 2 !== 0 && feature.image?.filename ? 'order-1 justify-self-end' : ''"
                    >
                        <TagComponent v-if="feature.title" class-name="mb-5 xl:mb-6">{{ feature.title }}</TagComponent>
                        <p
                            class="text-sm font-normal leading-relaxed text-[#393734] xl:text-lg xl:leading-[30px]"
                            :class="feature.image?.filename ? 'max-w-[159px] xl:max-w-[411px]' : 'max-w-md xl:max-w-[411px]'"
                        >
                            {{ feature.description }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
